import React, { useEffect, useRef, useState } from 'react';
import PageHeader from '../common/PageHeader';
import '../../styles/Experience.css';

interface FaqItem {
  question: string;
  answer: string;
}

const Experience = (): React.ReactElement => {
  const contentSectionRef = useRef<HTMLElement | null>(null);
  const spacesRef = useRef<HTMLElement | null>(null);
  const bringRef = useRef<HTMLElement | null>(null);
  const faqSectionRef = useRef<HTMLElement | null>(null);

  // State for accordion functionality
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  // Function to toggle accordion items
  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };
  // Function to check if elements are in viewport to apply fade effect
  const checkFade = () => {
    const refs = [contentSectionRef, spacesRef, bringRef, faqSectionRef];

    refs.forEach(ref => {
      if (ref.current) {
        const elementTop = ref.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;

        if (elementTop < windowHeight - 100) {
          ref.current.classList.add('active');
        }
      }
    });
  };

  // Set up fade effect on scroll
  useEffect(() => {
    // Initial check
    checkFade();

    // Add scroll event listener
    window.addEventListener('scroll', checkFade);

    // Clean up
    return () => {
      window.removeEventListener('scroll', checkFade);
    };
  }, []);

  // FAQ items for the accordion
  const faqItems: FaqItem[] = [
    {
      question: "What should I wear to a Body Sharing event?",
      answer: "We encourage sensual attire such as latex, leather, kinky. You should wear what makes you feel confident and comfortable. Don't forget to bring a change of clothes for departure."
    },
    {
      question: "Do I need to bring my own drinks and snacks?",
      answer: "Yes, Body Sharing events are BYOB (Bring Your Own Beverages) and snacks. We do not provide drinks or food at our events, so please come prepared with whatever you'd like to consume during your time with us."
    },
    {
      question: "Are there any restrictions on where I can eat or drink?",
      answer: "Yes, eating, drinking, and smoking are not allowed in the play area. We have designated areas for these activities, including our social area and smoking area where you can enjoy your refreshments."
    },
    {
      question: "What time should I arrive?",
      answer: "Arrival is between 21:00 and 23:00. We have a strict no late entry policy, so please plan to arrive within this window. This helps us maintain the atmosphere and ensure everyone receives a proper welcome and orientation."
    },
    {
      question: "How long do the events typically last?",
      answer: "Our events run from 21:00 until approximately 5:00 the next morning. The play area opens at 23:00 and remains available until 4:00, with the final hour dedicated to winding down and departing."
    },
    {
      question: "Do I need to bring protection (condoms, vaginal dams, etc.)?",
      answer: "Yes, you are responsible for bringing your own protection. We strongly encourage safe practices at all our events, and bringing your own supplies ensures you have what you need for your comfort and safety."
    },
    {
      question: "What is the QR code for and how do I get it?",
      answer: "Your personalized QR code is your ticket to the event and will be sent to you via email after your application is approved. This code is linked to your identity within our community and is required for entry."
    },
    {
      question: "Are there consent ambassadors at the events?",
      answer: "Yes, consent ambassadors are present at all our events to ensure everyone's boundaries are respected. They monitor interactions discreetly and are available if you need assistance or have concerns during the event."
    },
    {
      question: "What happens if someone makes me uncomfortable?",
      answer: "If at any point you feel uncomfortable, you can speak with one of our consent ambassadors immediately. We take all concerns seriously and will address them promptly to ensure everyone's boundaries and comfort are maintained."
    },
    {
      question: "Can I exchange contact information with people I meet?",
      answer: "Yes, you may exchange contacts with new connections if it's mutually desired. This typically happens during the farewell period (4:00-5:00) when attendees are preparing to leave."
    }
  ];

  return (
    <>

      <PageHeader
        title="The Experience"
        subtitle="What to expect at our intimate gatherings"
      />

      <section className="container fade-in" ref={contentSectionRef}>
        <h2 className="text-center">Your Sensual Journey Through Body Sharing</h2>
        <p className="text-center">Every Body Sharing gathering is meticulously curated to create a journey of sensual discovery.</p>
        <p className="text-center">Please bring your own drinks/snacks and protection.</p>

        <div className="experience-timeline">
          <div className="timeline-item timeline-left">
            <div className="timeline-content">
              <div className="timeline-time">21:00 - 23:00, no late entry</div>
              <h3>Arrival & Welcome</h3>
              <p>Check in with your personal QR code</p>
              <ul>
                <li>Arrive at our discreet location and complete entry protocols</li>
                <li>Change into your chosen attire of leather, lace, or latex</li>
                <li>Bring from home your own drinks and everything you need for the night (towel, protections)</li>
              </ul>
            </div>
          </div>

          <div className="timeline-item timeline-right">
            <div className="timeline-content">
              <div className="timeline-time">23:00 - 4:00</div>
              <h3>Play time</h3>
              <ul>
                <li>Access to all spaces opens, including our carefully designed play area</li>
                <li>Freedom to move between social spaces and more intimate areas as desired</li>
                <li>Consent ambassadors ensure everyone's boundaries are respected</li>
                <li>Do not eat, drink or smoke in the playarea</li>
              </ul>
            </div>
          </div>

          <div className="timeline-item timeline-left">
            <div className="timeline-content">
              <div className="timeline-time">4:00 - 5:00</div>
              <h3>Farewell</h3>
              <ul>
                <li>Return to the changing area to reclaim your outer world persona</li>
                <li>Exchange contacts with new connections if mutually desired</li>
                <li>For those whose energy remains high, information about after-parties or clubs is shared</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="spaces-section container fade-in" ref={spacesRef}>
        <h2 className="text-center">Our Curated Spaces</h2>
        <p className="text-center">Three distinct areas have been designed to accommodate different moods and desires throughout your journey:</p>

        <div className="spaces-grid">
          <div className="space-card">
            <div className="space-image">Social Area</div>
            <div className="space-content">
              <h3>The Social Area</h3>
              <p>Our primary gathering space where connections begin.</p>
              <div className="tags">
                <span className="tag">Mingling</span>
                <span className="tag">Conversations</span>
              </div>
            </div>
            <div className="space-footer">
              <i className="fas fa-volume-down"></i> Regular conversation welcome
            </div>
          </div>

          <div className="space-card">
            <div className="space-image">Smoking Area</div>
            <div className="space-content">
              <h3>The smoking area</h3>
              <p>A discrete space for those who wish to smoke.</p>
              <div className="tags">
                <span className="tag">Smoking</span>
                <span className="tag">Conversations</span>
              </div>
            </div>
            <div className="space-footer">
              <i className="fas fa-volume-down"></i> Quiet conversation appreciated
            </div>
          </div>

          <div className="space-card">
            <div className="space-image">Play Area</div>
            <div className="space-content">
              <h3>The Play Area</h3>
              <p>Our carefully designed space for deeper physical connections.</p>
              <div className="tags">
                <span className="tag">Intimate</span>
                <span className="tag">Private</span>
                <span className="tag">Deep Connection</span>
              </div>
            </div>
            <div className="space-footer">
              <i className="fas fa-volume-mute"></i> Hushed tones only
            </div>
          </div>
        </div>
      </section>

      <section className="container fade-in" ref={bringRef}>
        <h2 className="text-center">What to Bring</h2>
        <div className="bring-container">
          <div className="bring-category">
            <h3>1. Documentation</h3>
            <ul>           
              <li>Your personalized QR code (sent via email)</li>
            </ul>
          </div>

          <div className="bring-category">
            <h3>2. Attire</h3>
            <ul>
              <li>Your chosen sensual attire (latex, leather, lace, or elegant dress)</li>
              <li>A change of clothes for departure</li>
              <li>A towel</li>
            </ul>
          </div>

          <div className="bring-category">
            <h3>3. Other</h3>
            <ul>
              <li>Your own drinks</li>
              <li>Your own snacks</li>
              <li>Personal hygiene items</li>
              <li>Cash</li>
              <li>Protection (condoms, vaginal dams, etc.)</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="faq-section container fade-in" ref={faqSectionRef}>
        <h2 className="text-center">Frequently Asked Questions</h2>

        <style>{`
          /* Accordion Styling */
          .accordion {
            margin-top: 30px;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .accordion-item {
            border-bottom: 1px solid rgba(225, 190, 231, 0.3);
          }
          
          .accordion-header {
            padding: 18px 20px;
            background-color: rgba(156, 39, 176, 0.1);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background-color 0.3s;
          }
          
          .accordion-header:hover {
            background-color: rgba(156, 39, 176, 0.2);
          }
          
          .accordion-header h3 {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: #e1bee7;
          }
          
          .accordion-content {
            max-height: 0;
            overflow: hidden;
            background-color: rgba(30, 30, 30, 0.8);
            transition: max-height 0.5s ease;
          }
          
          .accordion-content.active {
            max-height: 1000px;
            padding: 20px;
          }
          
          .accordion-content p {
            color: #ffffff;
            margin: 0;
            line-height: 1.6;
          }
        `}</style>

        <div className="accordion">
          {faqItems.map((item, index) => (
            <div className="accordion-item" key={index}>
              <div
                className={`accordion-header ${activeAccordion === index ? 'active' : ''}`}
                onClick={() => toggleAccordion(index)}
              >
                <h3>{item.question}</h3>
                <span>{activeAccordion === index ? '-' : '+'}</span>
              </div>
              <div className={`accordion-content ${activeAccordion === index ? 'active' : ''}`}>
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default Experience;
